import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { ArrowLeft, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { EmptyState, IconButton, SearchBar, SegmentedControl, TopAppBar } from '@nokowazzi/ui';
import { ProductGridSkeleton, ProductGrid } from '../features/home/components/ProductGrid';
import { QueryErrorState } from '../features/home/components/QueryErrorState';
import { RequestList, RequestListSkeleton } from '../features/home/components/RequestList';
import { useProductSearchQuery, useRequestSearchQuery } from '../features/home/queries';

const searchParamsSchema = z.object({
  q: z.string().trim().min(1).max(100).optional().catch(undefined),
  // optional로 두어 /search 네비게이션에 search 파라미터 강제되지 않게 한다
  tab: z.enum(['products', 'requests']).optional().catch(undefined),
});

type SearchTab = 'products' | 'requests';

export const Route = createFileRoute('/search')({
  validateSearch: (search) => searchParamsSchema.parse(search),
  component: SearchScreen,
});

function SearchScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const navigate = useNavigate({ from: '/search' });
  const { q, tab: tabParam } = Route.useSearch();
  const tab: SearchTab = tabParam ?? 'products';
  const [input, setInput] = useState(q ?? '');

  // 뒤로가기/딥링크로 q가 바뀌면 입력창 동기화
  useEffect(() => {
    setInput(q ?? '');
  }, [q]);

  const goBack = () => {
    if (window.history.length > 1) router.history.back();
    else void navigate({ to: '/' });
  };

  const submit = (value: string) => {
    const trimmed = value.trim();
    void navigate({
      search: (prev) => ({ ...prev, q: trimmed === '' ? undefined : trimmed }),
      replace: true,
    });
  };

  const changeTab = (next: SearchTab) => {
    void navigate({ search: (prev) => ({ ...prev, tab: next }), replace: true });
  };

  const query = q ?? '';
  const productsQuery = useProductSearchQuery(tab === 'products' ? query : '');
  const requestsQuery = useRequestSearchQuery(tab === 'requests' ? query : '');
  const activeQuery = tab === 'products' ? productsQuery : requestsQuery;

  const goProduct = (productId: string) =>
    void navigate({ to: '/products/$productId', params: { productId } });
  const goRequest = (requestId: string) =>
    void navigate({ to: '/sourcing/$requestId', params: { requestId } });

  return (
    <div className="flex min-h-dvh flex-col pb-[var(--safe-bottom)]">
      <TopAppBar
        leading={
          <IconButton aria-label={t('actions.back')} onClick={goBack}>
            <ArrowLeft />
          </IconButton>
        }
      />
      <div className="flex flex-col gap-3 px-5 pt-1">
        <SearchBar
          value={input}
          onValueChange={setInput}
          onSubmit={submit}
          clearLabel={t('actions.clear')}
          placeholder={t('home.searchPlaceholder')}
          autoFocus
        />
        <SegmentedControl<SearchTab>
          options={[
            { value: 'products', label: t('search.products') },
            { value: 'requests', label: t('search.requests') },
          ]}
          value={tab}
          onChange={changeTab}
        />
      </div>

      <div className="flex-1 px-5 pt-4 pb-6">
        {q === undefined ? (
          <EmptyState
            className="mt-10"
            icon={<Search size={48} strokeWidth={1.5} />}
            title={t('home.searchPlaceholder')}
          />
        ) : activeQuery.isPending ? (
          tab === 'products' ? (
            <ProductGridSkeleton />
          ) : (
            <RequestListSkeleton />
          )
        ) : activeQuery.isError ? (
          <QueryErrorState
            className="mt-10"
            error={activeQuery.error}
            onRetry={() => void activeQuery.refetch()}
          />
        ) : activeQuery.data.items.length === 0 ? (
          <EmptyState
            className="mt-10"
            icon={<Search size={48} strokeWidth={1.5} />}
            title={t('search.noResults')}
          />
        ) : tab === 'products' && productsQuery.data !== undefined ? (
          <ProductGrid products={productsQuery.data.items} onSelect={goProduct} />
        ) : requestsQuery.data !== undefined ? (
          <RequestList requests={requestsQuery.data.items} onSelect={goRequest} />
        ) : null}
      </div>
    </div>
  );
}
